import {
  Alert,
  Anchor,
  Box,
  Button,
  Code,
  CopyButton,
  Group,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCheck, IconCopy, IconLock } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { IME_APLIKACIJE } from '../ime';
import { datum, denar, tocke } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { cas } from '../skrbnistvo/skrbnistvo';
import { NAZIVI_PAKETOV, OPISI_PAKETOV, type Paket } from '../seja/paket';
import { seja } from '../seja/seja';
import { useSeja } from '../seja/useSeja';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { meseci, opisGibanja, VRSTE_GIBANJ, ZAKLENJENA } from './narocnina';
import { useZaklep, zaklep } from './zaklep';
import { ZnackaNarocnine } from './ZnackaNarocnine';

/**
 * Naročnina (uporabniški meni pod imenom; v zaklepu edini zaslon): stanje točk in do kdaj je plačano, nakazilo na naš
 * račun (UPN QR za znesek po izbiri s točkami zanj, sklic, vidno najkasneje v treh dneh) in prek Stripa (samodejno, s stroškom obdelave) s kopiranjem,
 * lestvica bonusov in zgodovina točk. Vse izračuna strežnik ({@code GET /api/narocnina}); ob
 * prvem odprtju strežnik pripravi IBAN pri Stripu. Paket naročnine lahko stranka razširi ({@link RazsiritevPaketa}).
 */
export function Narocnina() {
  const stanje = useSeja();
  const sporociloZaklepa = useZaklep();
  const narocnina = useQuery({
    queryKey: ['narocnina'],
    queryFn: ({ signal }) => podatki(api.GET('/api/narocnina', { signal })),
    // po nakazilu se točke pripišejo v nekaj minutah – v zaklepu preverjamo pogosteje
    refetchInterval: (q) => (ZAKLENJENA.includes(q.state.data?.stanje ?? '') ? 30_000 : false),
  });
  const n = narocnina.data;
  const zaklenjeno = n ? ZAKLENJENA.includes(n.stanje ?? '') : !!sporociloZaklepa;

  // po plačilu (stanje brez zaklepa) se aplikacija odklene
  useEffect(() => {
    if (n && !ZAKLENJENA.includes(n.stanje ?? '')) {
      zaklep.odkleni();
    }
  }, [n]);

  const naziv = stanje.status === 'prijavljen' ? stanje.instanca.naziv : '';
  const zaVklop = n ? (n.narocnina ?? 0) + (n.ponovniVklop ?? 0) : 0;

  return (
    <ZaslonSeznama>
      <GlavaZaslona
        naslov="Naročnina"
        opis="Stanje točk, plačilo z nakazilom in zgodovina."
        pomoc="narocnina"
        dejanja={n && <ZnackaNarocnine stanje={n.stanje} />}
      />
      <Stack gap="md">
        {zaklenjeno && (
          <Alert color="red" variant="filled" radius="lg" icon={<IconLock size={22} />} title="Dostop je zaklenjen">
            {n
              ? `Naročnina je potekla${n.placanoDo ? ` (plačano do ${datum(n.placanoDo)})` : ''}. Za odklep nakažite vsaj ${denar(zaVklop)} € (mesečna naročnina in ${denar(n.ponovniVklop)} € za ponovni vklop). Dostop se odklene sam, ko nakazilo prispe – prek Stripa samodejno, na naš račun najkasneje v treh dneh.`
              : sporociloZaklepa}
          </Alert>
        )}
        {!n ? (
          <Kartica>
            <StanjeNalaganja
              napaka={narocnina.error}
              ponovi={() => void narocnina.refetch()}
              naslov="Naročnine ni bilo mogoče naložiti"
            />
          </Kartica>
        ) : (
          <>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <Kartica naslov="Stanje">
                <Podatki
                  vrstice={[
                    ['Paket', n.paket ? NAZIVI_PAKETOV[n.paket] : ''],
                    ['Točke', `${tocke(n.tocke)} točk`],
                    ['Pokrijejo', n.mesecevPokrije ? meseci(n.mesecevPokrije) : 'manj kot mesec'],
                    ['Mesečna naročnina', `${denar(n.narocnina)} €`],
                    ['Plačano do', n.placanoDo ? datum(n.placanoDo) : 'brez omejitve'],
                    ['Naslednji obračun', n.naslednjiObracun ? datum(n.naslednjiObracun) : ''],
                  ]}
                />
                <Text size="sm" c="dimmed" mt="sm">
                  1 € nakazila je 1 točka. Na dan obračuna se odšteje mesečna naročnina; če točk ni dovolj, imate še 10
                  dni, nato se dostop zaklene, dokler ne nakažete.
                </Text>
              </Kartica>
              {(n.bonusi ?? []).length > 0 && (
                <Kartica naslov="Bonus za večja nakazila">
                  <Text size="sm" mb="xs">
                    Za posamezno nakazilo vsaj danega zneska dobite dodatne točke:
                  </Text>
                  <Table withRowBorders={false} verticalSpacing={4} maw={360}>
                    <Table.Tbody>
                      {(n.bonusi ?? []).map((b) => (
                        <Table.Tr key={b.odZneska}>
                          <Table.Td>od {denar(b.odZneska)} €</Table.Td>
                          <Table.Td ta="right" fw={600}>
                            +{denar(b.odstotek)} % točk
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Kartica>
              )}
            </SimpleGrid>
            {(n.razsiritve ?? []).length > 0 && (
              <RazsiritevPaketa
                razsiritve={n.razsiritve ?? []}
                narocnina={n.narocnina}
                naslednjiObracun={n.naslednjiObracun}
                prijavaKot={stanje.status === 'prijavljen' && stanje.prijavaKot}
              />
            )}
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              {n.trr && <NakaziloNaRacun key={n.trr.znesek ?? ''} trr={n.trr} naziv={naziv ?? ''} />}
              <Kartica naslov="Nakazilo prek Stripa">
                {n.nakazilo ? (
                  <Stack gap="xs">
                    <Text size="sm">
                      Točke se pripišejo samodejno, ko nakazilo prispe (SEPA nakazilo običajno v enem delovnem dnevu).
                      {n.stripeStrosek
                        ? ` Za obdelavo zaračunamo ${denar(n.stripeStrosek)} € na nakazilo (odšteje se od točk).`
                        : ''}
                    </Text>
                    <Vrstica oznaka="Prejemnik" vrednost={n.nakazilo.imetnik ?? ''} />
                    <Vrstica oznaka="IBAN" vrednost={n.nakazilo.iban ?? ''} koda />
                    <Vrstica oznaka="BIC" vrednost={n.nakazilo.bic ?? ''} koda />
                    <Vrstica oznaka="Namen" vrednost={`${IME_APLIKACIJE} ${naziv}`.trim()} />
                    <Text size="xs" c="dimmed">
                      Ta plačila za nas sprejema Stripe, zato je prejemnik Stripe in IBAN tuj ({n.nakazilo.drzava}). Gre
                      za navadno SEPA nakazilo v evrih; račun je samo vaš, zato sklic ni potreben.
                    </Text>
                  </Stack>
                ) : (
                  <Alert color="yellow" variant="light">
                    {n.nakaziloNi ?? 'Podatki za nakazilo trenutno niso na voljo.'}
                  </Alert>
                )}
              </Kartica>
            </SimpleGrid>
            <Kartica naslov="Zgodovina točk">
              {(n.gibanja ?? []).length === 0 ? (
                <Text size="sm" c="dimmed">
                  Še ni gibanj.
                </Text>
              ) : (
                <Table.ScrollContainer minWidth={640}>
                  <Table verticalSpacing={6}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Čas</Table.Th>
                        <Table.Th>Vrsta</Table.Th>
                        <Table.Th ta="right">Točke</Table.Th>
                        <Table.Th ta="right">Stanje</Table.Th>
                        <Table.Th>Opis</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {(n.gibanja ?? []).map((g) => (
                        <Table.Tr key={g.id}>
                          <Table.Td className="brez-preloma">{cas(g.cas)}</Table.Td>
                          <Table.Td>{VRSTE_GIBANJ[g.vrsta ?? ''] ?? g.vrsta}</Table.Td>
                          <Table.Td ta="right" className="brez-preloma">
                            <Text size="sm" fw={600} c={(g.tocke ?? 0) < 0 ? 'red' : 'green'}>
                              {(g.tocke ?? 0) > 0 ? '+' : ''}
                              {tocke(g.tocke)}
                            </Text>
                          </Table.Td>
                          <Table.Td ta="right" className="brez-preloma">
                            {tocke(g.stanjePo)}
                          </Table.Td>
                          <Table.Td>
                            {g.obdobjeOd
                              ? `${datum(g.obdobjeOd)} – ${datum(g.obdobjeDo)}`
                              : opisGibanja(g.znesek, g.komentar)}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
            </Kartica>
          </>
        )}
      </Stack>
    </ZaslonSeznama>
  );
}

/**
 * Nakazilo na naš račun: znesek (privzeto ga predlaga strežnik – naročnina, v zaklepu še ponovni vklop) lahko stranka
 * spremeni; strežnik vrne UPN QR s tem zneskom in točke zanj ({@code GET /api/narocnina/trr}). Dokler nova koda ne
 * pride, je stara zatemnjena, ob napaki je ni, da ne bi kdo plačal napačnega zneska.
 */
function NakaziloNaRacun({ trr, naziv }: { trr: Shema['PodatkiZaTrr']; naziv: string }) {
  const [znesek, setZnesek] = useState<number | string>(trr.znesek ?? '');
  const [zakasnjeno] = useDebouncedValue(znesek, 400);
  const privzeto = zakasnjeno === (trr.znesek ?? '');
  const izbrani = useQuery({
    queryKey: ['narocnina', 'trr', zakasnjeno],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/narocnina/trr', { params: { query: { znesek: Number(zakasnjeno) || 0 } }, signal })),
    enabled: !privzeto,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const p = privzeto ? trr : izbrani.data;
  const napaka = !privzeto && izbrani.isError ? izbrani.error : null;
  const caka = znesek !== zakasnjeno || (!privzeto && (izbrani.isFetching || izbrani.isPlaceholderData));
  const napakaZneska =
    napaka instanceof NapakaApi ? (napaka.polja.znesek ?? sporocilo(napaka)) : napaka && sporocilo(napaka);

  return (
    <Kartica naslov="Nakazilo na naš račun">
      <Stack gap="xs">
        <Text size="sm">
          Brez dodatnih stroškov. Vpišite znesek in poskenirajte kodo UPN QR v mobilni banki ali prepišite podatke.
          Plačate lahko tudi s trajnim nalogom.
        </Text>
        <Group align="flex-start" gap="md" wrap="wrap">
          <Box w={220} h={220} style={{ flexShrink: 0, opacity: caka ? 0.35 : 1, transition: 'opacity 150ms' }}>
            {p?.upnQr && !napaka ? (
              <img
                src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(p.upnQr)}`}
                alt="UPN QR koda za nakazilo"
                width={220}
                height={220}
              />
            ) : (
              <Text size="sm" c="dimmed" ta="center" pt={90}>
                Vpišite znesek
              </Text>
            )}
          </Box>
          <Stack gap="xs" style={{ flex: '1 1 220px', minWidth: 0 }}>
            <NumberInput
              size="md"
              label="Znesek"
              value={znesek}
              onChange={setZnesek}
              min={0}
              decimalScale={2}
              fixedDecimalScale
              decimalSeparator=","
              thousandSeparator="."
              suffix=" €"
              hideControls
              error={napakaZneska}
              description={
                napaka || p?.tocke == null
                  ? '1 € je 1 točka, za večja nakazila še bonus.'
                  : caka
                    ? 'Dobite … točk.'
                    : `Dobite ${denar(p.tocke)} točk${p.bonus ? ` (${denar(p.znesek)} + ${denar(p.bonus)} bonus)` : ''}.`
              }
            />
            <Vrstica oznaka="Prejemnik" vrednost={trr.prejemnik ?? ''} />
            <Vrstica oznaka="IBAN" vrednost={trr.iban ?? ''} koda />
            <Vrstica oznaka="Sklic" vrednost={trr.sklic ?? ''} koda />
            <Vrstica oznaka="Namen" vrednost={trr.namen ?? ''} />
          </Stack>
        </Group>
        <Text size="xs" c="dimmed">
          {trr.prejemnik}
          {trr.ulica ? `, ${trr.ulica}` : ''}, {trr.kraj}
        </Text>
        <Alert color="blue" variant="light" p="xs">
          <Text size="sm">
            Sprememba je vidna najkasneje v treh dneh.
            {trr.email && (
              <>
                {' '}
                Če želite hitreje, nam potrdilo o plačilu pošljite na{' '}
                <Anchor
                  href={`mailto:${trr.email}?subject=${encodeURIComponent(`Plačilo naročnine – ${naziv} (${trr.sklic ?? ''})`)}`}
                >
                  {trr.email}
                </Anchor>
                .
              </>
            )}
          </Text>
        </Alert>
      </Stack>
    </Kartica>
  );
}

/**
 * Razširitev paketa (dodajanje modula): ob vsakem paketu opis in mesečna cena po ceniku, »Dodaj« zahteva potrditev.
 * Moduli veljajo takoj (seja se osveži, da se pokaže meni), doplačilo do naslednjega obračuna (izračuna ga strežnik) se
 * takoj odšteje od točk, nova cena velja od naslednjega obračuna; posebna cena ne velja več. Med prijavo kot uporabnik
 * ni mogoče (paket ureja skrbništvo).
 */
function RazsiritevPaketa({
  razsiritve,
  narocnina,
  naslednjiObracun,
  prijavaKot,
}: {
  razsiritve: Shema['CenaPaketa'][];
  narocnina: number | undefined;
  naslednjiObracun: string | undefined;
  prijavaKot: boolean;
}) {
  const odjemalec = useQueryClient();
  const [izbran, setIzbran] = useState<Paket | null>(null);
  const razsiri = useMutation({
    mutationFn: (paket: Paket) => podatki(api.PUT('/api/narocnina/paket', { body: { paket } })),
    onSuccess: (n) => {
      odjemalec.setQueryData(['narocnina'], n);
      setIzbran(null);
      void seja.osvezi();
      notifications.show({
        color: 'green',
        message: `Paket je razširjen na ${n.paket ? NAZIVI_PAKETOV[n.paket] : ''}. Novi moduli so na voljo takoj.`,
      });
    },
  });
  return (
    <Kartica naslov="Razširite paket">
      <Stack gap="sm">
        {razsiritve.map((r) =>
          r.paket ? (
            <Paper key={r.paket} withBorder radius="md" p="sm">
              <Group justify="space-between" align="flex-start" gap="sm">
                <Stack gap={2} style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <Text fw={700}>{NAZIVI_PAKETOV[r.paket]}</Text>
                  <Text size="sm" c="dimmed">
                    {OPISI_PAKETOV[r.paket]}
                  </Text>
                  <Text size="sm" fw={600}>
                    {denar(r.cena)} € na mesec z DDV
                  </Text>
                </Stack>
                {!prijavaKot && izbran !== r.paket && (
                  <Button variant="light" onClick={() => setIzbran(r.paket ?? null)}>
                    Dodaj
                  </Button>
                )}
              </Group>
              {izbran === r.paket && (
                <Stack gap="xs" mt="sm">
                  <Text size="sm">
                    Moduli bodo na voljo takoj.
                    {r.doplacilo
                      ? ` Zdaj se odšteje ${tocke(r.doplacilo)} točk – razlika do novega paketa za dni do naslednjega obračuna${naslednjiObracun ? ` (${datum(naslednjiObracun)})` : ''}.`
                      : ''}{' '}
                    Od naslednjega obračuna bo mesečna naročnina {denar(r.cena)} €
                    {narocnina != null ? ` (zdaj ${denar(narocnina)} €)` : ''}.
                  </Text>
                  {razsiri.error && (
                    <Text size="sm" c="red">
                      {sporocilo(razsiri.error)}
                    </Text>
                  )}
                  <Group gap="xs">
                    <Button loading={razsiri.isPending} onClick={() => r.paket && razsiri.mutate(r.paket)}>
                      Potrdi razširitev
                    </Button>
                    <Button variant="subtle" onClick={() => setIzbran(null)} disabled={razsiri.isPending}>
                      Prekliči
                    </Button>
                  </Group>
                </Stack>
              )}
            </Paper>
          ) : null,
        )}
        {prijavaKot && (
          <Text size="sm" c="dimmed">
            Med prijavo kot uporabnik paketa ni mogoče spremeniti – uredite ga v skrbništvu.
          </Text>
        )}
      </Stack>
    </Kartica>
  );
}

/** Podatek za nakazilo s kopiranjem (na telefonu oznaka nad vrednostjo). */
function Vrstica({ oznaka, vrednost, koda = false }: { oznaka: string; vrednost: string; koda?: boolean }) {
  return (
    <Group justify="space-between" gap="xs" wrap="nowrap">
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size="xs" c="dimmed">
          {oznaka}
        </Text>
        {koda ? (
          <Code fz="sm" style={{ overflowWrap: 'anywhere' }}>
            {vrednost}
          </Code>
        ) : (
          <Text size="sm" fw={500} style={{ overflowWrap: 'anywhere' }}>
            {vrednost}
          </Text>
        )}
      </Stack>
      <CopyButton value={vrednost}>
        {({ copied, copy }) => (
          <Button
            size="compact-sm"
            variant="subtle"
            leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            onClick={copy}
            aria-label={`Kopiraj ${oznaka}`}
            style={{ flexShrink: 0 }}
          >
            {copied ? 'Kopirano' : 'Kopiraj'}
          </Button>
        )}
      </CopyButton>
    </Group>
  );
}
